// Authentication context for InfluenceHub MVP
// Manages user authentication state and role-based access
// Subscribes to Firebase Auth and Firestore profile data
// Uses tri-state model: loading, authenticated, unauthorized
// Does NOT aggressively sign out users - lets route guards handle redirects

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig.js';
import { needsBackfill, backfillOnboardingCompleted } from '../lib/onboardingLegacy';

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  // State for user authentication and profile
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to Firebase Auth state changes
  // Note: Persistence is set in firebaseConfig.js at app initialization
  useEffect(() => {
    console.log('🔐 AuthContext: Setting up Firebase auth listener...');
    
    let unsubscribe;
    let unsubscribeProfile = null;
    
    try {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('🔐 AuthContext: Firebase auth state changed');
        
        // Clean up previous profile subscription if it exists
        if (unsubscribeProfile) {
          unsubscribeProfile();
          unsubscribeProfile = null;
        }
        
        if (firebaseUser) {
          // User is signed in
          console.log('✅ AuthContext: User is signed in:', firebaseUser.uid);
          setUser(firebaseUser);
          setLoading(true);
          
          // Subscribe to user profile in Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          console.log('📄 AuthContext: Subscribing to user profile:', firebaseUser.uid);
          
          unsubscribeProfile = onSnapshot(userDocRef, async (docSnapshot) => {
            try {
              if (docSnapshot.exists()) {
                const userData = { id: docSnapshot.id, ...docSnapshot.data() };
                // Legacy bypass: backfill onboarding completed for pre-existing users
                if (needsBackfill(firebaseUser, userData)) {
                  backfillOnboardingCompleted(firebaseUser.uid).then(() => {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[onboarding-legacy] AuthContext: backfill triggered for', firebaseUser.uid);
                    }
                  }).catch((err) => {
                    console.warn('[onboarding-legacy] AuthContext backfill failed (non-blocking):', err);
                  });
                }
                console.log('📋 AuthContext: User profile loaded:', userData.userType, userData.status);
                
                // NOTE: Approval workflow has been removed.
                // 'status' is kept only for backward compatibility and analytics, not for gating access.
                // We no longer mutate user documents to enforce status.
                const status = userData.status || 'approved'; // Keep for backwards compatibility
                
                // Set profile - status is informational only, not used for access control
                setProfile({ ...userData, status });
                console.log('✅ User profile loaded (status:', status, '- informational only)');
              } else {
                // Profile doesn't exist in Firestore
                // This could mean:
                // 1. User hasn't completed signup
                // 2. Profile was deleted
                // 3. Error case
                // Do NOT sign out - let route guards handle redirecting to signup/login
                console.log('⚠️ User profile does not exist in Firestore');
                console.log('⚠️ Route guards will handle redirecting to appropriate page');
                setProfile(null);
              }
            } catch (error) {
              console.error('Error processing user profile:', error);
              setProfile(null);
            } finally {
              setLoading(false);
            }
          }, (error) => {
            console.error('Error fetching user profile:', error);
            // Do NOT sign out on Firestore errors - just set profile to null
            // Route guards will handle showing appropriate UI
            setProfile(null);
            setLoading(false);
          });
        } else {
          // User is signed out
          console.log('🔐 AuthContext: User is signed out');
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('❌ AuthContext: Error setting up Firebase auth:', error);
      setLoading(false);
    }

    // Cleanup function for auth subscription
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      // State will be updated by the onAuthStateChanged listener
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Context value
  const value = {
    user,
    profile,
    loading,
    signOut
  };

  console.log('🔐 AuthContext: Rendering with user:', !!user, 'loading:', loading, 'profile:', !!profile);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
